<template>
  <lb-drawer data-testid="transaction-matching-config-new__add-edit-rule-drawer" :value="value" @input="$emit('input', $event)" :heading="`${(addeditdata.__type === 'add') ? 'Add Rule' : 'Edit Rule'}`" Maxwidth="40%" :loading="loading">
    <template v-slot:body>
      <div>
        <v-row>
          <v-col cols="6">
            <div class="d-flex align-center">
              <lb-dropdown data-testid="transaction-matching-config-new__rule-match-flag-dropdown" class="flux-grow-1 mr-1" label="Match Flag" hidedetails :items="rulenaturelist" v-model="addeditdata.nature" />
              <lb-helpmenu data-testid="transaction-matching-config-new__rule-match-flag-help">
                <template #text>
                  <div class="pa-4">
                    <span class="font-weight-bold mr-1">Automated Matching:</span>
                    <span class="">In the case of automated matching, a predefined rule is applied to automatically consider transactions (line-items) as matched. Once the rule conditions are met, the transactions are automatically paired without requiring manual intervention or approval.</span>
                    <br />
                    <br />
                    <span class="font-weight-bold mr-1">Suggested Matching:</span>
                    <span class="">In the case of suggested matching, a set of rules is used to suggest potential matches between transactions (line-items). However, these suggested matches go through a process of approval before being considered final. The suggested matches are presented to users for review and validation, allowing them to confirm or modify the proposed matches based on their expertise and judgment.</span>
                    <br />
                  </div>
                </template>
              </lb-helpmenu>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="d-flex align-center">
              <lb-dropdown data-testid="transaction-matching-config-new__rule-relation-dropdown" class="flux-grow-1 mr-1" label="Relation" hidedetails :items="ruletypelist" v-model="addeditdata.type" />
              <lb-helpmenu data-testid="transaction-matching-config-new__rule-relation-help">
                <template #text>
                  <div class="pa-4">
                    <span class="font-weight-bold mr-1">1:1 Matching:</span>
                    <span class="">This scenario involves matching one transaction (line-item) with another transaction. Each transaction is paired with another transaction, resulting in a one-to-one relationship.</span>
                    <br />
                    <br />
                    <span class="font-weight-bold mr-1">1:M Matching:</span>
                    <span class="">In this scenario, one transaction (line-item) is matched with a group of transactions. The transaction is associated with multiple related transactions, resulting in a one-to-many relationship.</span>
                    <br />
                    <br />
                    <span class="font-weight-bold mr-1">M:1 Matching:</span>
                    <span class="">In the M:1 scenario, a group of transactions (line-items) is matched with one transaction. Multiple transactions are associated with single transactions, resulting in a many-to-one relationship.</span>
                    <br />
                    <br />
                    <span class="font-weight-bold mr-1">M:M Matching:</span>
                    <span class="">This scenario involves matching a group of transactions (line-items) with another group of transactions. Each transaction within one group is paired with multiple transactions within another group, resulting in a many-to-many relationship.</span>
                  </div>
                </template>
              </lb-helpmenu>
            </div>
          </v-col>
          <v-col cols="12">
            <div data-testid="transaction-matching-config-new__string-matching-section" class="font-weight-bold mb-1">String matching rule</div>
            <template v-for="(v, k) in columns">
              <div v-if="v.type === 'string'" :key="k" data-testid="transaction-matching-config-new__string-match-row" class="d-flex align-top" :id="'automation-test-'+ k">
                <v-checkbox data-testid="transaction-matching-config-new__string-match-checkbox" dense hide-details class="ma-0 pa-0 mr-2" :value="k" v-model="localSelectedRuleColumn" multiple></v-checkbox>
                <div>
                  <div>
                    <v-chip data-testid="transaction-matching-config-new__string-match-chip-data1" :id="'automation-test-'+ k" small label class="px-2 font-weight-bold white"><span>{{ v.data1 }}</span></v-chip>
                    <span class="mx-2">with</span>
                    <v-chip data-testid="transaction-matching-config-new__string-match-chip-data2" :id="'automation-test-'+ k" small label class="px-2 font-weight-bold white"><span>{{ v.data2 }}</span></v-chip>
                  </div>
                  <div v-if="localSelectedRuleColumn.indexOf(k) > -1" class="mb-2 d-flex mt-2">
                    <lb-dropdown data-testid="transaction-matching-config-new__string-match-type-dropdown" :id="'automation-test-'+ k" class="mr-1" label="Type" hidedetails :items="strmatchtype" @change="onDropdownChange" v-model="addeditdatacols[k].type" />
                    <lb-dropdown data-testid="transaction-matching-config-new__string-match-column-dropdown" :id="'automation-test-'+ k" v-if="addeditdatacols[k].type && addeditdatacols[k].type !== 'exact'" class="mr-1" label="In Column" @change="onDropdownChange" hidedetails :items="[{ name: '1 - ' + columns[k]?.data1, value: 'first' }, { name: '2 - ' + columns[k]?.data2, value: 'second' }]" v-model="addeditdatacols[k].in" />
                    <lb-number data-testid="transaction-matching-config-new__string-match-ratio-input" :id="'automation-test-'+ k" min="0" v-if="addeditdatacols[k].type === 'approximate match'" prependiconinner="mdi-percent" class="mr-1" label="Match %" @change="onDropdownChange" hidedetails v-model="addeditdatacols[k].matchratio" />
                  </div>
                </div>
              </div>
            </template>
          </v-col>
          <v-col cols="12">
            <div data-testid="transaction-matching-config-new__number-date-matching-section" class="font-weight-bold mb-1">Number & Date matching rule</div>
            <template v-for="(v, k) in columns">
              <div v-if="v.type === 'number' || v.type === 'date'" :key="k" data-testid="transaction-matching-config-new__number-date-match-row" class="d-flex align-top" :id="'automation-test-'+ k">
                <v-checkbox data-testid="transaction-matching-config-new__number-date-match-checkbox" dense hide-details class="ma-0 pa-0 mr-2" :value="k" v-model="localSelectedRuleColumn" multiple></v-checkbox>
                <div>
                  <div>
                    <v-chip data-testid="transaction-matching-config-new__number-date-match-chip-data1" :id="'automation-test-'+ k" small label class="px-2 font-weight-bold white"><span>{{ v.data1 }}</span></v-chip>
                    <span class="mx-2">with</span>
                    <v-chip data-testid="transaction-matching-config-new__number-date-match-chip-data2" :id="'automation-test-'+ k" small label class="px-2 font-weight-bold white"><span>{{ v.data2 }}</span></v-chip>
                  </div>
                  <div v-if="localSelectedRuleColumn.indexOf(k) > -1" class="mb-2 d-flex mt-2">
                    <lb-number data-testid="transaction-matching-config-new__number-date-range-input" :id="'automation-test-'+ k" :min="(v.type === 'number' && addeditdatacols[k].negative)  ? '' : '0'" class="mr-1" :label="`${(v.type === 'number') ? 'Number ' : 'Days'} Range`" @change="onDropdownChange" hidedetails v-model="addeditdatacols[k].type" />
                  </div>
                  <div v-if="localSelectedRuleColumn.indexOf(k) > -1">
                    <v-checkbox data-testid="transaction-matching-config-new__opposite-sign-checkbox" :id="'automation-test-'+ k" v-if="v.type === 'number'" dense label="Opposite Sign Match" v-model="addeditdatacols[k].negative" @change="onDropdownChange"></v-checkbox>
                  </div>
                </div>
              </div>
            </template>
          </v-col>
          <v-col cols="12" v-if="addeditdata.type === 'M:1' || addeditdata.type === '1:M' || addeditdata.type === 'M:M'">
            <div data-testid="transaction-matching-config-new__grouping-section" class="font-weight-bold mb-1">Grouping rule</div>
            <div class="ml-2">
              <div data-testid="transaction-matching-config-new__grouping-criteria-label">
                <v-icon>mdi-circle-small</v-icon>Criteria considered for grouping
              </div>
              <div class="ml-4 mb-2">
                <div data-testid="transaction-matching-config-new__grouping-file1" v-if="addeditdata.type === 'M:1' || addeditdata.type === 'M:M'">
                  <template v-for="(v, k) in localSelectedRuleColumn">
                    <v-chip data-testid="transaction-matching-config-new__grouping-chip-file1" :id="'automation-test-'+ k + '-group-file1'" :key="k" v-if="columns[v].type === 'string' || columns[v].type === 'date'" small label class="px-2 font-weight-bold white"><span>{{ columns[v].data1 }}</span></v-chip>
                  </template>
                  <span class="ml-1">in {{ filenames[0] }}</span>
                </div>
                <div data-testid="transaction-matching-config-new__grouping-file2" v-if="addeditdata.type === '1:M' || addeditdata.type === 'M:M'">
                  <template v-for="(v, k) in localSelectedRuleColumn">
                    <v-chip data-testid="transaction-matching-config-new__grouping-chip-file2" :id="'automation-test-'+ k + '-group-file2'" :key="k" v-if="columns[v].type === 'string' || columns[v].type === 'date'" small label class="px-2 font-weight-bold white"><span>{{ columns[v].data1 }}</span></v-chip>
                  </template>
                  <span class="ml-1">in {{ filenames[1] }}</span>
                </div>
              </div>
              <div data-testid="transaction-matching-config-new__aggregation-label">
                <v-icon>mdi-circle-small</v-icon>Aggregation Methods<span v-if="isAggregationMandatory" class="red--text ml-1">*</span>
              </div>
              <div class="ml-4">
                <template v-for="(v, k) in columns">
                  <div data-testid="transaction-matching-config-new__aggregation-row" :id="'automation-test-'+ k + '-agg-row'" v-if="v.type === 'number' && localSelectedRuleColumn.indexOf(k) > -1" :key="k">
                    <div class="d-flex align-center mb-1">
                      <v-chip data-testid="transaction-matching-config-new__aggregation-chip-data1" :id="'automation-test-'+ k + '-agg-chip-data1'" v-if="addeditdata.type === 'M:1' || addeditdata.type === 'M:M'" small label class="px-2 font-weight-bold white"><span>{{ v.data1 }}</span></v-chip>
                      <span class="mx-2" v-if="addeditdata.type === 'M:M'">contains in</span>
                      <v-chip data-testid="transaction-matching-config-new__aggregation-chip-data2" :id="'automation-test-'+ k + '-agg-chip-data2'" v-if="addeditdata.type === '1:M' || addeditdata.type === 'M:M'" small label class="px-2 font-weight-bold white"><span>{{ v.data2 }}</span></v-chip>
                      <lb-dropdown data-testid="transaction-matching-config-new__aggregation-method-dropdown" :id="'automation-test-'+ k + '-agg-method'" class="ml-1" label="Method" hidedetails :items="aggregationtype" @change="onDropdownChange" v-model="aggregationtypesection[k]" :error="aggregationError" />
                      <v-spacer class="flex-grow-1"></v-spacer>

                    </div>
                  </div>
                </template>
              </div>
              <div v-if="isAggregationMandatory && aggregationError" class="red--text caption mt-2" data-testid="transaction-matching-config-new__aggregation-error">
                {{ aggregationError }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
    <template v-slot:actions>
      <span class="caption">*Required</span>
      <v-spacer></v-spacer>
      <v-btn data-testid="transaction-matching-config-new__add-rule-button" color="primary" v-if="addeditdata.__type === 'add'" @click="$emit('save-rule', 'add')" class=""><span>Add</span></v-btn>
      <v-btn data-testid="transaction-matching-config-new__edit-rule-button" color="primary" v-else @click="$emit('save-rule', 'edit')" class=""><span>Edit</span></v-btn>
    </template>
  </lb-drawer>
</template>

<script>
export default {
  name: 'RuleConfigDrawer',
  props: {
    value: Boolean,
    addeditdata: Object,
    loading: Boolean,
    rulenaturelist: Array,
    ruletypelist: Array,
    columns: Object,
    selectedrulecolumn: Array,
    addeditdatacols: Object,
    strmatchtype: Array,
    filenames: Array,
    isAggregationMandatory: Boolean,
    aggregationtype: Array,
    aggregationtypesection: Object,
    aggregationError: String,
    analysisId: { type: String, default: '' },
    pid: { type: String, default: '' },
    bid: { type: String, default: '' },
  },
  data() {
    return {
      analysisdata: {},
      analysisloading: false,
      availableColumnLabels: [],
      selectedColumnLabel: null,
    };
  },
  watch: {
    value(newVal) {
      if (newVal && this.analysisId) {
        this.loadAnalysisData();
      }
    },
  },
  mounted() {
    console.log('[RuleConfigDrawer]: mounted with analysisdata:', this.analysisdata);
  },
  computed: {
    localSelectedRuleColumn: {
      get() {
        return this.selectedrulecolumn;
      },
      set(val) {
        this.$emit('update:selectedrulecolumn', val);
      }
    }
  },
  methods: {
    async loadAnalysisData() {
      this.analysisloading = true;
      try {
        const response = await this.axios.post(
          `/v2/bot/analysis/loadTransactionMatchingAnalysis/${this.analysisId}`,
          {
            data: {
              pid: this.pid,
              bid: this.bid,
            },
          }
        );
        console.log('[RuleConfigDrawer]: loadAnalysisData response.data:', response.data);
        if (response.data.status === 'success') {
          this.analysisdata = response.data.data?.[0] || {};          
          // Extract labels from every entity's column map
          const extractedLabels = [];
          
          // Check if fileconfig exists inside setup since the response is an array of objects
          const fileConfigData = this.analysisdata?.setup?.fileconfig || {};

          Object.values(fileConfigData).forEach(entity => {
            if (entity && Array.isArray(entity.columnmap)) {
              entity.columnmap.forEach(col => {
                // Normalize: [internalKey, displayLabel] tuple, object, or plain string
                let labelName;
                if (Array.isArray(col)) {
                  labelName = col[1] || col[0];
                } else if (col && typeof col === 'object') {
                  labelName = col.label || col.name || col.header;
                } else {
                  labelName = col;
                }
                const normalized = typeof labelName === 'string' ? labelName.trim() : null;
                if (normalized && !extractedLabels.some(l => l.value === normalized)) {
                  extractedLabels.push({ name: normalized, value: normalized });
                }
              });
            }
          });
          this.availableColumnLabels = extractedLabels;
          console.log('[RuleConfigDrawer]: all entity column map labels:', extractedLabels);
          console.log('[RuleConfigDrawer]: analysisdata set to:', this.analysisdata.setup?.fileconfig);
          this.$emit('analysis-loaded', this.analysisdata);
        } else {
          console.error('[RuleConfigDrawer]: loadAnalysisData failed', response.data);
        }
      } catch (err) {
        console.error('[RuleConfigDrawer]: loadAnalysisData error', err);
      } finally {
        this.analysisloading = false;
      }
    },
    onDropdownChange() {
      // Logic to trigger Vue reactivity or force update, as seen in the original code:
      // selectedrulecolumn.push('xxx'); selectedrulecolumn.pop()
      // We emit the update to the parent to handle this if needed, 
      // but modifying the array reference by value might be enough if parent is reactive.
      // However, to mimic exactly:
      const temp = [...this.localSelectedRuleColumn, 'xxx'];
      temp.pop();
      this.$emit('update:selectedrulecolumn', temp);
    }
  }
}
</script>
